import React, { createContext, useContext, useState, useEffect } from "react";

type User = {
  id: number;
  name: string;
  email: string;
  token: string;
};

type AuthContextType = {
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Verifica se o usuário está logado ao iniciar a aplicação
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const getClientIp = async (): Promise<string> => {
    try {
      const response = await fetch("https://api.ipify.org?format=json");
      const data = await response.json();
      return data.ip;
    } catch (error) {
      console.error("Erro ao obter o IP do cliente:", error);
      return "IP não disponível";
    }
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    const client_ip = await getClientIp();

    const response = await fetch(
      "https://app.sak.com.br/CentralAjudaApi/login",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password, client_ip }),
      }
    );

    const data = await response.json();

    if (data.success) {
      const loggedUser = {
        id: data.user.id,
        name: data.user.name,
        email: data.user.email,
        token: data.token,
      };

      setUser(loggedUser);
      localStorage.setItem("user", JSON.stringify(loggedUser));

      return true;
    }

    return false;
  };

  const logout = async (): Promise<boolean> => {
    const response = await fetch(
      "https://app.sak.com.br/CentralAjudaApi/logout",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.token}`,
        },
      }
    );

    const data = await response.json();

    if (data.success) {
      setUser(null);
      localStorage.removeItem("user");

      return true;
    }

    return false;
  };

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {loading ? null : children}{" "}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
